/**
 * Notification message templates for admin trip actions
 * Used by DeliveryIssueToastProvider to generate consistent notification messages
 * Feature 26112: Admin Trip Block/Unblock/Cancel Push Notifications
 */

export const NotificationMessages = {
    /**
     * Message shown to drivers when admin blocks their trip
     */
    TRIP_BLOCKED_DRIVER: (tripId: number): string =>
        `Trip #${tripId} has been blocked. Please wait for admin instructions.`,

    /**
     * Message shown to admins when another admin blocks a trip
     */
    TRIP_BLOCKED_ADMIN: (tripId: number): string =>
        `Trip #${tripId} blocked by Admin`,

    /**
     * Message shown to drivers when admin unblocks their trip
     */
    TRIP_UNBLOCKED_DRIVER: (tripId: number): string =>
        `Trip #${tripId} has been unblocked. You can continue the trip.`,

    /**
     * Message shown to admins when another admin unblocks a trip
     */
    TRIP_UNBLOCKED_ADMIN: (tripId: number): string =>
        `Trip #${tripId} unblocked by Admin`,

    /**
     * Message shown to drivers when admin cancels their trip
     */
    TRIP_CANCELLED_DRIVER: (tripId: number, reason: string): string =>
        `Trip #${tripId} has been cancelled. ${reason}`,

    /**
     * Message shown to admins when a trip is cancelled
     */
    TRIP_CANCELLED_ADMIN: (tripId: number, reason: string): string =>
        `Trip #${tripId} cancelled - Reason: ${reason}`,

    /**
     * Message shown when a single request is removed from an active trip
     */
    REQUEST_REMOVED_FROM_TRIP: (
        tripId: number,
        requestId: number,
        returnRequired: boolean
    ): string =>
        returnRequired
            ? `Request #${requestId} was removed from Trip #${tripId}. The driver must return the picked-up items to the origin before the request is canceled.`
            : `Request #${requestId} was removed from Trip #${tripId} and marked as canceled. Review and adjust the route as needed.`,

    /**
     * Message shown to admins when a trip is deleted (Feature 27406)
     */
    TRIP_DELETED_ADMIN: (tripId: number, deletedBy: string): string =>
        `Trip #${tripId} has been permanently deleted by ${deletedBy}.`,

    /**
     * Message header for blocked trips
     */
    HEADER_BLOCKED: "Trip Blocked",

    /**
     * Message header for unblocked trips
     */
    HEADER_UNBLOCKED: "Trip Unblocked",

    /**
     * Message header for cancelled trips
     */
    HEADER_CANCELLED: "Trip Cancelled",

    /**
     * Message header for deleted trips (Feature 27406)
     */
    HEADER_DELETED: "Trip Deleted",

    /**
     * Message header for request removal route updates
     */
    HEADER_REQUEST_REMOVED: "Request Removed from Active Trip",

    /**
     * Message header for critical issues (admin notification banner)
     */
    HEADER_CRITICAL: "Critical Issue Reported",
};

/**
 * Notification type constants
 */
export const NotificationTypes = {
    TRIP_ADMIN_BLOCK: "trip_admin_block",
    TRIP_ADMIN_UNBLOCK: "trip_admin_unblock",
    TRIP_ADMIN_CANCEL: "trip_admin_cancel",
    REQUEST_REMOVED_FROM_TRIP: "request_removed_from_trip",
    TRIP_ADMIN_DELETE: "trip_admin_delete", // Feature 27406: Trip deletion notification
    DELIVERY_BLOCKED: "delivery_blocked", // Existing
    TRIP_STATUS_CHANGE: "trip_status_change", // Existing
    TRIP_CREATED: "trip_created", // Existing
} as const;

export type NotificationType =
    (typeof NotificationTypes)[keyof typeof NotificationTypes];
